import { useCallback, useRef, useEffect } from "react";
import { performUpdateInbound } from "../../../../graphql/inbound/inboundCRUD";
import { performUpdateOutbound } from "../../../../graphql/outbound/outboundCRUD";
import {
  NoteType,
  OutboundStatus,
  ResourceType,
  UNSPOTTED_TRACK_ID,
} from "../../../../utils/constants";
import {
  stripTrackCarFields,
  ensureCompositeKeyFields,
} from "../../../../utils/trackCarPersistence";
import { getLocationFullName } from "../../../../utils/locationUtils";
import { useTrackOperations } from "../../../../utils/useTrackOperations";
import { applyInboundSpotPlacement } from "../../../../utils/shipmentPlacement";

/**
 * Hook for managing track location operations in the Crew App.
 * Now leverages the shared useTrackOperations for core state and persistence logic.
 */
export const useTrackLocationOperations = ({
  // State
  selectedShipment,
  selectedTrack,
  processedCarsMap,
  outboundMap,
  // Refs
  selectedTrackRef,
  processedCarsMapRef,
  // Setters
  setLoading,
  setProcessedCarsMap,
  setSelectedShipment,
  // View state
  isTabletOrLandscape = false,
  // Functions
  isOutbound,
  refreshTracksDebounced,
  getShipments,
  notify,
  notifyError,
  logError,
  tracks = [],
}) => {
  // CRITICAL: Stable refs for state to ensure handler stability
  const selectedShipmentRef = useRef(selectedShipment);
  const selectedTrackInternalRef = useRef(selectedTrack);

  useEffect(() => {
    selectedShipmentRef.current = selectedShipment;
  }, [selectedShipment]);

  useEffect(() => {
    selectedTrackInternalRef.current = selectedTrack;
  }, [selectedTrack]);

  // Save shipment (inventory/inventory item location updates handled by handleTrackCarChange Lambda)
  const saveShipment = useCallback(
    async (toUpdateShipment) => {
      const shipmentIsOutbound = isOutbound(toUpdateShipment);
      const shipmentWithCompositeKeys = ensureCompositeKeyFields(
        toUpdateShipment,
        shipmentIsOutbound,
      );
      const sanitizedShipment = stripTrackCarFields(shipmentWithCompositeKeys);

      if (shipmentIsOutbound) {
        return performUpdateOutbound(sanitizedShipment);
      } else {
        return performUpdateInbound(sanitizedShipment);
      }
    },
    [isOutbound],
  );

  const resolveParentType = useCallback(
    (shipment) => {
      if (!shipment) return null;
      if (
        shipment.parentType !== ResourceType.INBOUND &&
        shipment.parentType !== ResourceType.OUTBOUND
      ) {
        logError(
          "trackLocation:resolveParentType:missingParentType",
          new Error("Missing canonical parentType on shipment payload"),
          notify,
        );
        return null;
      }
      return shipment.parentType;
    },
    [logError, notify],
  );

  const resolveTrackByLocationId = useCallback(
    (trackLocationId) => {
      if (!trackLocationId) return null;
      return (
        (tracks || []).find(
          (track) => track?.locations?.items?.[0]?.id === trackLocationId,
        ) || null
      );
    },
    [tracks],
  );

  // Updates shipment status/dates/history when a railcar is placed on track.
  // For drag-drop, targetTrackId is the canonical placement context.
  // trackCarShipment is TrackCar from processedCarsMap; extract shipment for API.
  // Resolves to {railCarNumber, reason} when the car was spotted but its inbound
  // could not be placed, so the caller can report a whole drop at once.
  const updateShipmentOnPlacement = useCallback(
    async (trackCarShipment, previousStatus, targetTrackId = null) => {
      if (!trackCarShipment) return;
      const shipment =
        trackCarShipment.inbound ?? trackCarShipment.outbound ?? trackCarShipment;
      const isOut = isOutbound(trackCarShipment);
      const trackForLocation = targetTrackId
        ? resolveTrackByLocationId(targetTrackId)
        : selectedTrackInternalRef.current;

      if (targetTrackId && !trackForLocation) {
        logError(
          "trackLocation:updateShipmentOnPlacement:targetTrackNotFound",
          new Error(`Could not resolve target track ${targetTrackId}`),
          notify,
        );
      }
      const location = trackForLocation?.locations?.items?.[0];
      const locationFullName = location ? getLocationFullName(location) : null;
      const placementHistoryText = locationFullName
        ? `Railcar placed on track ${locationFullName}`
        : "Railcar placed on track";

      if (isOut) {
        const toUpdate = {
          ...shipment,
          parentType: trackCarShipment.parentType ?? ResourceType.OUTBOUND,
          active: shipment.active ?? "true",
        };
        if (toUpdate.status == null) {
          logError(
            "trackLocation:updateShipmentOnPlacement:missingOutboundStatus",
            new Error("Missing outbound status on root shipment payload"),
            notify,
          );
          return;
        }
        if (
          toUpdate.status === OutboundStatus.PENDING ||
          toUpdate.status === OutboundStatus.ARRIVED
        ) {
          toUpdate.status = OutboundStatus.PLACED;
          // Same reset the dispatch form's save applies when it derives
          // PLACED — a stale REJECTED state or demurrage count must not
          // survive a fresh spotting.
          toUpdate.state = "";
          toUpdate.daysOnDemurrageClock = 0;
          // Spotting is the placement event. arrivalDateTime (gate arrival)
          // is filled only when nothing stamped it — rail has no gate
          // check-in, and it sorts the by-dates list GSIs.
          if (!toUpdate.placementDateTime)
            toUpdate.placementDateTime = new Date().toISOString();
          if (!toUpdate.arrivalDateTime)
            toUpdate.arrivalDateTime = toUpdate.placementDateTime;
        }
        toUpdate.history = [
          ...(toUpdate.history || []),
          {
            type: NoteType.SHIPMENT,
            text: placementHistoryText,
            date: new Date().toISOString(),
            by: window.appUser?.fullName,
          },
        ];
        await saveShipment(toUpdate);
      } else {
        const toUpdate = {
          ...shipment,
          parentType: trackCarShipment.parentType ?? ResourceType.INBOUND,
          railCarNumber:
            shipment.railCarNumber ??
            trackCarShipment.railCarNumber ??
            "",
        };
        if (toUpdate.status == null || toUpdate.state == null) {
          logError(
            "trackLocation:updateShipmentOnPlacement:missingInboundStatusState",
            new Error("Missing inbound status/state on root shipment payload"),
            notify,
          );
          return;
        }
        // Status/state advance and the history note both come from the shared
        // helper, which also decides whether placement is refused. The caller
        // reports the refusal — one drop can refuse several cars at once.
        const refusal = applyInboundSpotPlacement(toUpdate, {
          locationFullName,
        });
        await saveShipment(toUpdate);
        if (refusal)
          return { railCarNumber: toUpdate.railCarNumber, reason: refusal };
      }
    },
    [
      isOutbound,
      saveShipment,
      resolveTrackByLocationId,
      logError,
      notify,
    ],
  );

  // Core Operations from Shared Hook
  const {
    addItem: addItemBase,
    removeItem: removeItemBase,
    deleteItem: deleteItemBase,
  } = useTrackOperations({
    trackData: processedCarsMap,
    setTrackData: setProcessedCarsMap,
    setLoading,
    saveShipment,
    notify,
    logError,
    unspottedTrackId: UNSPOTTED_TRACK_ID,
    refreshTracks: (ids) => refreshTracksDebounced(ids),
    getShipments,
    resolveParentType,
    tracks,
  });

  const addItem = useCallback(
    async (idx) => {
      if (!selectedShipmentRef.current?.id || !selectedTrackInternalRef.current)
        return;
      await addItemBase({
        shipment: selectedShipmentRef.current,
        targetTrack: selectedTrackInternalRef.current,
        index: idx,
        updateShipmentOnPlacement,
      });
      setSelectedShipment(null);
    },
    [addItemBase, setSelectedShipment, updateShipmentOnPlacement],
  );

  const removeFromTrack = useCallback(
    async (idx, trackLocationIdOverride = null, shipmentIdOverride = null) => {
      let shipmentId = shipmentIdOverride;
      let trackLocationId =
        trackLocationIdOverride ||
        selectedTrackInternalRef.current?.locations?.items?.[0]?.id;

      if (idx >= 0 && trackLocationId && !shipmentId) {
        const car = processedCarsMap[trackLocationId]?.[idx];
        if (car) shipmentId = car.id;
      }

      if (!shipmentId || !trackLocationId) {
        logError(
          "trackLocation:removeFromTrack:missingRequired",
          new Error(
            `Missing required shipmentId or trackLocationId (shipmentId=${shipmentId ?? "null"}, trackLocationId=${trackLocationId ?? "null"})`,
          ),
          notify,
        );
        return;
      }

      await removeItemBase({
        shipmentId,
        trackLocationId,
        trackDataOverride: processedCarsMapRef.current,
        updateShipmentOnPlacement: async () => {
          // Additional logic for removal if needed
          if (notify && !isTabletOrLandscape)
            notify("Railcar removed from track", "info");
        },
      });
    },
    [
      removeItemBase,
      processedCarsMap,
      notify,
      logError,
      isTabletOrLandscape,
      processedCarsMapRef,
    ],
  );

  const deleteItem = useCallback(
    async (shipmentToDelete) => {
      const shipmentId = shipmentToDelete?.id;
      const trackLocationId =
        shipmentToDelete?.trackLocationId ||
        selectedTrackInternalRef.current?.locations?.items?.[0]?.id;
      if (!shipmentId || !trackLocationId) {
        logError(
          "trackLocation:deleteItem:missingRequired",
          new Error(
            `Missing required shipmentId or trackLocationId (shipmentId=${shipmentId ?? "null"}, trackLocationId=${trackLocationId ?? "null"})`,
          ),
          notify,
        );
        return;
      }
      await deleteItemBase({ shipmentId, trackLocationId });
    },
    [deleteItemBase, logError, notify],
  );

  return {
    addItem,
    removeFromTrack,
    deleteItem,
    saveShipment,
    updateShipmentOnPlacement,
  };
};
